import {
  Button as ChakraButton,
  Flex,
  List,
  ListItem,
  Text,
  Tooltip
} from '@chakra-ui/react';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ButtonText,
  Checkbox,
  SearchInput,
  Menu,
  MenuButton,
  MenuList
} from '@morressier/alembic';
import {
  MANDATORY_AUTHOR_SUBMISSION_FIELDS,
  MandatoryAuthorSubmissionField
} from '@publishing-engine/submission-service/src/fixtures/mandatory-form-fields';
import { useEffect, useMemo, useRef, useState } from 'react';

type AllowedFormatsMultiSelectProps = {
  options: string[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  isDisabled?: boolean;
  currentField: string;
};

const AllowedFormatsMultiSelect: React.FC<AllowedFormatsMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  isDisabled = false,
  currentField
}) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);
  const [selectedValuesAtOpen, setSelectedValuesAtOpen] = useState<string[]>(
    []
  );

  const filteredItems = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return options;

    return options.filter(item =>
      item.toLowerCase().includes(normalizedSearch)
    );
  }, [options, search]);

  const isFieldMandatory = MANDATORY_AUTHOR_SUBMISSION_FIELDS.includes(
    currentField as MandatoryAuthorSubmissionField
  );

  const orderedItems = useMemo(() => {
    if (!selectedValuesAtOpen.length)
      return isFieldMandatory ? [] : filteredItems;

    const selectedSet = new Set(selectedValuesAtOpen);
    const selected: string[] = [];
    const unselected: string[] = [];

    // For mandatory fields, show only selected options
    if (isFieldMandatory) {
      return filteredItems.filter(item => selectedSet.has(item));
    }

    filteredItems.forEach(item => {
      if (selectedSet.has(item)) {
        selected.push(item);
        return;
      }
      unselected.push(item);
    });

    return [...selected, ...unselected];
  }, [filteredItems, selectedValuesAtOpen, isFieldMandatory]);

  useEffect(() => {
    if (!buttonRef.current) return;
    const { width } = buttonRef.current.getBoundingClientRect();
    setMenuWidth(width);
  }, []);

  const handleToggle = (value: string) => {
    const isSelected = selectedValues.includes(value);
    const nextValues = isSelected
      ? selectedValues.filter(item => item !== value)
      : [...selectedValues, value];

    onChange(nextValues);
  };

  const handleClear = () => {
    onChange([]);
  };

  const handleSelectAll = () => {
    const nextValues = Array.from(new Set([...selectedValues, ...filteredItems]));
    onChange(nextValues);
  };

  const allFilteredSelected =
    filteredItems.length > 0 &&
    filteredItems.every(item => selectedValues.includes(item));

  const previewValues = selectedValues.slice(0, 15);
  const remainingCount = selectedValues.length - previewValues.length;

  const buttonText = !selectedValues.length
    ? 'Select formats'
    : remainingCount > 0
    ? `${previewValues.join(', ')} +${remainingCount}`
    : previewValues.join(', ');

  const tooltipText = selectedValues.join(', ');

  const handleClose = () => {
    setMenuOpen(false);
    setSearch('');
  };

  return (
    <Menu
      ariaLabel="allowed file format"
      isOpen={menuOpen}
      onClose={handleClose}
    >
      <Tooltip
        label={tooltipText}
        placement="top"
        hasArrow
        shouldWrapChildren
        isDisabled={!selectedValues.length}
      >
        <MenuButton
          w={'full'}
          as={ChakraButton}
          rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
          textAlign="left"
          variant="outline"
          ref={buttonRef}
          disabled={isDisabled}
          onClick={() =>
            setMenuOpen(prevOpen => {
              const nextOpen = !prevOpen;
              if (nextOpen) {
                setSelectedValuesAtOpen(selectedValues);
              }
              return nextOpen;
            })
          }
        >
          <Text textStyle="textBody" isTruncated>
            {buttonText}
          </Text>
        </MenuButton>
      </Tooltip>

      <MenuList
        maxH="320px"
        overflowY="auto"
        width={menuWidth ? `${menuWidth}px` : 'auto'}
        p={2}
      >
        <SearchInput
          placeholder="Search file format"
          value={search}
          onChange={event => setSearch(event.target.value)}
          onInputClear={() => setSearch('')}
          name="allowed-file-format-search"
        />

       <Flex gap={5}>
         <ButtonText
          onClick={handleSelectAll}
          variant="secondary"
          mb={2}
          mt={2}
          disabled={
            isFieldMandatory || filteredItems.length === 0 || allFilteredSelected
          }
        >
          Select All
        </ButtonText>

        <ButtonText
          onClick={handleClear}
          variant="caution"
          mb={2}
          mt={2}
          disabled={isFieldMandatory || !selectedValues.length}
        >
          Clear Selections
        </ButtonText>

       </Flex>
        <List spacing={1}>
          {orderedItems.map(item => (
            <ListItem key={item}>
              <Checkbox
                name={item}
                id={item}
                ariaLabel={item}
                isChecked={selectedValues.includes(item)}
                onChange={() => handleToggle(item)}
                disabled={isFieldMandatory}
              >
                {item}
              </Checkbox>
            </ListItem>
          ))}

          {!orderedItems.length ? (
            <Text color="gray.500" fontSize="xs">
              No results found
            </Text>
          ) : null}
        </List>
      </MenuList>
    </Menu>
  );
};

export default AllowedFormatsMultiSelect;
